import {useQuery} from '@tanstack/react-query';
import {InstantAnswer, SearchService} from 'client';
import {getService} from 'utilities';

export type InstantAnswerSummaryResult = {
  summary: string | null;
  latencyMs: number;
};

/**
 * One sentence answering `answeredQuery` from a knowledge answer's article
 * (`GET /api/v1/search/instant_answer/summary`). Asked only while the input
 * still reads `answeredQuery`: the next keystroke changes the key, and React
 * Query aborts the request it no longer needs.
 */
export const useInstantAnswerSummary = ({
  answer,
  answeredQuery,
  liveQuery,
}: {
  answer: InstantAnswer | null;
  answeredQuery: string;
  liveQuery: string;
}) => {
  const articleId = answer?.kind === InstantAnswer.kind.KNOWLEDGE ? answer.article_id : null;
  const query = liveQuery.trim();
  return useQuery({
    queryKey: ['search', 'instant-answer-summary', articleId, query],
    queryFn: async ({signal}): Promise<InstantAnswerSummaryResult> => {
      const started = performance.now();
      const request = getService(
        SearchService
      ).searchInstantAnswerSummaryApiV1SearchInstantAnswerSummaryGet(query, articleId!);
      signal.addEventListener('abort', () => request.cancel(), {once: true});
      const response = await request;
      return {summary: response.summary ?? null, latencyMs: performance.now() - started};
    },
    enabled: !!articleId && query.length > 0 && query === answeredQuery,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};
